import { zodResolver } from '@hookform/resolvers/zod';
import { type User } from '@kalos/kalos-rpc';
import {
  Button,
  Checkbox,
  Form as FormContext,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@kalos/ui';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import { RESIDENTIAL_OPTIONS, USA_STATES_OPTIONS } from '../constants';
import { useFieldLayout } from '../hooks/useMedia';
import { AddressSearchInput } from './AddressSearchInput';
import {
  type PropertyFormData,
  propertyFormSchema,
  propertyToFormValues,
} from './propertyFormUtils';

export interface PropertyFormProps {
  customer?: User;
  defaultValues?: PropertyFormData;
  onSubmit: (data: PropertyFormData) => void;
  submitting?: boolean;
  submitLabel: string;
  submitClassName?: string;
  footerStart?: ReactNode;
  showContactSection?: boolean;
}

export const PropertyForm = ({
  customer,
  defaultValues,
  onSubmit,
  submitting = false,
  submitLabel,
  submitClassName,
  footerStart,
  showContactSection = false,
}: PropertyFormProps) => {
  const [showContact, setShowContact] = useState(false);
  // Phones and tablets: one column, the optional location fields folded behind one row.
  const isFieldLayout = useFieldLayout();
  const [showLocationDetails, setShowLocationDetails] = useState(false);
  const columns = isFieldLayout ? 'flex flex-col gap-4' : 'grid grid-cols-3 gap-4';
  const [sameAsCustomer, setSameAsCustomer] = useState(false);

  const form = useForm<PropertyFormData>({
    resolver: zodResolver(propertyFormSchema),
    mode: 'onChange',
    defaultValues: defaultValues ?? propertyToFormValues(),
  });

  const handleSubmit: SubmitHandler<PropertyFormData> = (data) => onSubmit(data);

  return (
    <FormContext {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <fieldset disabled={submitting} className="space-y-4">
          <h3 className="border-b pb-1 text-sm font-semibold text-gray-700">Address Details</h3>
          <div className={columns}>
            <FormField
              control={form.control}
              name="streetAddress"
              render={({ field }) => (
                <FormItem className="col-span-2">
                  <FormLabel>Address *</FormLabel>
                  <FormControl>
                    <AddressSearchInput
                      value={field.value}
                      onChange={field.onChange}
                      onSelect={(a) => {
                        form.setValue('streetAddress', a.address, { shouldValidate: true });
                        form.setValue('city', a.city, { shouldValidate: true });
                        form.setValue('state', a.state, { shouldValidate: true });
                        form.setValue('zipCode', a.zip, { shouldValidate: true });
                      }}
                      placeholder="Search address..."
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>City *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="City" className="h-10" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className={columns}>
            <FormField
              control={form.control}
              name="state"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>State *</FormLabel>
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="State" />
                      </SelectTrigger>
                      <SelectContent>
                        {USA_STATES_OPTIONS.map((s) => {
                          const val = typeof s === 'string' ? s : (s as any).value;
                          const label = typeof s === 'string' ? s : (s as any).label;
                          return (
                            <SelectItem key={String(val)} value={String(val)}>
                              {String(label)}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="zipCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>ZIP Code *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="12345 or 12345-6789" className="h-10" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="storeNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Store/Unit Number (optional)</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="e.g. 1234" className="h-10" />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="propertyOwnershipTypeId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Property Ownership *</FormLabel>
                <FormControl>
                  <Tabs value={field.value} onValueChange={field.onChange} className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="1">Owner-Occupied</TabsTrigger>
                      <TabsTrigger value="2">Tenant-Occupied</TabsTrigger>
                    </TabsList>
                  </Tabs>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {isFieldLayout && !showLocationDetails ? (
            <button
              type="button"
              onClick={() => setShowLocationDetails(true)}
              className="active:bg-muted flex min-h-12 w-full items-center justify-between rounded-md border border-dashed px-3 text-left text-sm font-medium"
            >
              <span>Add location details (optional)</span>
              <ChevronRight className="h-4 w-4" aria-hidden />
            </button>
          ) : (
            <>
              <h3 className="border-b pb-1 text-sm font-semibold text-gray-700">
                Location Details
              </h3>
              <div className={columns}>
                <FormField
                  control={form.control}
                  name="directions"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Directions</FormLabel>
                      <FormControl>
                        <Input {...field} className="h-10" />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="subdivision"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Subdivision</FormLabel>
                      <FormControl>
                        <Input {...field} className="h-10" />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="isResidential"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Zoning</FormLabel>
                      <FormControl>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger>
                            <SelectValue placeholder="Zoning" />
                          </SelectTrigger>
                          <SelectContent>
                            {RESIDENTIAL_OPTIONS.map((o) => {
                              const val = typeof o === 'string' ? o : (o as any).value;
                              const label = typeof o === 'string' ? o : (o as any).label;
                              return (
                                <SelectItem key={String(val)} value={String(val)}>
                                  {String(label)}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <h3 className="border-b pb-1 text-sm font-semibold text-gray-700">Notes</h3>
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="Any special instructions or details..."
                        className="h-24"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </>
          )}

          {showContactSection && (
            <div className="rounded-md border border-dashed">
              <button
                type="button"
                onClick={() => setShowContact((s) => !s)}
                className="flex w-full items-center justify-between p-3 text-left text-sm font-medium"
              >
                <span>Add point of contact (optional)</span>
                {showContact ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
              {showContact && (
                <div className="space-y-3 border-t p-3">
                  {customer && (
                    <label className="flex w-fit cursor-pointer items-center gap-2 text-sm">
                      <Checkbox
                        checked={sameAsCustomer}
                        onCheckedChange={(v) => {
                          const checked = v === true;
                          setSameAsCustomer(checked);
                          if (checked) {
                            form.setValue(
                              'contactName',
                              `${customer.firstname ?? ''} ${customer.lastname ?? ''}`.trim(),
                            );
                            form.setValue(
                              'contactPhone',
                              customer.phone || customer.cellphone || '',
                            );
                          } else {
                            form.setValue('contactName', '');
                            form.setValue('contactPhone', '');
                          }
                        }}
                      />
                      Same as customer
                    </label>
                  )}
                  <FormField
                    control={form.control}
                    name="contactName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Contact name" className="h-10" />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="contactPhone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="Phone" className="h-10" />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="contactTitle"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Title</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="e.g. Property Manager"
                              className="h-10"
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    When filled, this is saved as the property&apos;s who-to-call contact (shown on
                    jobs).
                  </p>
                </div>
              )}
            </div>
          )}

          <div
            className={
              isFieldLayout ? 'flex flex-col-reverse gap-2 pt-2' : 'flex justify-between pt-2'
            }
          >
            {footerStart ?? <span />}
            <Button
              type="submit"
              disabled={!form.formState.isValid || submitting}
              isLoading={submitting}
              className={isFieldLayout ? `${submitClassName ?? ''} w-full` : submitClassName}
            >
              {submitLabel}
            </Button>
          </div>
        </fieldset>
      </form>
    </FormContext>
  );
};
